import type { CSSProperties, ReactNode } from "react";
import { buildingById, itemById, researchNodeById } from "@zend/content";
import { pick, t, useT } from "../../i18n/index.js";
import { useBalances } from "../../state/balances.js";
import { useSession } from "../../state/session.js";
import { Button } from "../../ui/Button.js";
import { formatCompact } from "../../ui/Counter.js";
import { formatDuration, useServerTick } from "../../ui/Timer.js";
import type { Cost, ResourceKey } from "../api.js";
import { GameIcon, ResourceIcon } from "../../assets/gameIcons.js";
import { Icon } from "../../ui/Icon.js";
import { Dialog } from "../../ui/Dialog.js";

const RESOURCE_ORDER: ResourceKey[] = ["RAT", "POL", "ALY", "ENC", "SCR"];

export const buildingName = (id: string): string => pick(buildingById(id).name);
export const buildingDescription = (id: string): string => pick(buildingById(id).description);
export const itemName = (id: string): string => pick(itemById(id).name);
export const researchName = (id: string): string => pick(researchNodeById(id).name);

/** Bottom sheet above the navigation; one open at a time (R10 mobile-first panels). */
export function Sheet({ title, onClose, children, testId, actions, footer, wide = false }: { title: ReactNode; onClose: () => void; children: ReactNode; testId?: string; actions?: ReactNode; footer?: ReactNode; wide?: boolean }) {
  return <Dialog title={title} onClose={onClose} actions={actions} footer={footer} wide={wide} testId={testId} surface="sheet" modal={false}>{children}</Dialog>;
}

/** Resource costs with affordability colours (R10 panels, R50 store feel). */
export function CostRow({ cost, style, compact = false }: { cost: Partial<Cost>; style?: CSSProperties; compact?: boolean }) {
  const balances = useBalances((s) => s.balances);
  return (
    <span className="row" style={{ gap: compact ? 6 : 10, flexWrap: "wrap", ...style }} data-testid="cost-row">
      {RESOURCE_ORDER.filter((k) => (cost[k] ?? 0) > 0).map((k) => {
        const need = cost[k] ?? 0;
        const have = balances ? balances[k] : 0;
        const short = balances !== null && have < need;
        return (
          <span key={k} className="mono" style={{ fontSize: compact ? 12 : 13, color: short ? "#fca5a5" : "var(--text)", display: "inline-flex", gap: 4, alignItems: "center" }} title={t(`res.${k}`)}>
            <ResourceIcon resource={k} size={compact ? 16 : 18} label={t(`res.${k}`)} />
            {formatCompact(need)}
          </span>
        );
      })}
    </span>
  );
}

export function canAfford(cost: Partial<Cost>): boolean {
  const b = useBalances.getState().balances;
  if (!b) return false;
  return RESOURCE_ORDER.every((k) => (cost[k] ?? 0) <= b[k]);
}

/** Translates the server's short reason strings (English tokens) into the UI language. */
export function reasonText(reason: string): string {
  const gate = /^querschnitt ([\d.]+) < (\d+)/.exec(reason);
  if (gate) return t("base.reason.gate", { avg: gate[1]!, req: gate[2]! });
  const cap = /^HQ level (\d+) caps/.exec(reason);
  if (cap) return t("base.reason.hqcap", { n: cap[1]! });
  const tier = /^Data Lab tier (\d+) < (\d+)/.exec(reason);
  if (tier) return t("research.reason.tier", { have: tier[1]!, need: tier[2]! });
  const req = /^requires (rsh\.\S+)/.exec(reason);
  if (req) return t("research.reason.requires", { name: researchName(req[1]!) });
  const map: Record<string, string> = {
    busy: "base.reason.busy",
    "max level": "base.reason.max",
    "insufficient resources": "base.reason.resources",
    "not upgradeable": "base.reason.static",
    researching: "research.reason.busy",
    "Data Lab required": "research.reason.lab",
    "queue busy": "base.reason.queue",
  };
  const key = map[reason];
  return key ? t(key) : reason;
}

export function Reasons({ reasons }: { reasons: string[] }) {
  if (reasons.length === 0) return null;
  return (
    <ul className="reasons" data-testid="reasons">
      {reasons.map((r) => (
        <li key={r}>{reasonText(r)}</li>
      ))}
    </ul>
  );
}

/** Progress bar from start to finish in server time. */
export function Progress({ startedAt, finishesAt }: { startedAt: number | null; finishesAt: number }) {
  const now = useServerTick();
  const start = startedAt ?? now;
  const p = finishesAt <= start ? 1 : Math.min(1, Math.max(0, (now - start) / (finishesAt - start)));
  return (
    <div className="progress" role="progressbar" aria-valuenow={Math.round(p * 100)} aria-valuemin={0} aria-valuemax={100}>
      <div className="progress-fill" style={{ width: `${p * 100}%` }} />
    </div>
  );
}

/** Speedup items in the inventory that apply to the given scope, with one-tap use (R12). */
export function SpeedupList({ scope, onUse, testId = "speedups" }: { scope: "build" | "research" | "repair" | "fabrication"; onUse: (itemId: string, qty: number) => void; testId?: string }) {
  const tt = useT();
  const inventory = useSession((s) => s.inventory);
  const items = inventory
    .filter((i) => i.qty > 0 && i.itemId.startsWith("itm.speedup_"))
    .map((i) => ({ ...i, def: itemById(i.itemId) }))
    .filter((i) => i.def.params["type"] === scope || i.def.params["type"] === "general")
    .sort((a, b) => Number(a.def.params["minutes"]) - Number(b.def.params["minutes"]));
  if (items.length === 0) return <p className="hint">{tt("base.speedup.none")}</p>;
  return (
    <div className="row" style={{ flexWrap: "wrap", gap: 6 }} data-testid={testId}>
      {items.map((i) => (
        <Button key={i.itemId} onClick={() => onUse(i.itemId, 1)} style={{ minHeight: 36, padding: "0 12px", fontSize: 12 }} data-testid={`speedup-${i.itemId}`}>
          <GameIcon id={i.itemId} size={20} label={pick(i.def.name)} /> {formatDuration(Number(i.def.params["minutes"]) * 60_000)} <span className="dim">×{i.qty}</span>
        </Button>
      ))}
    </div>
  );
}

/** Instant-finish price: one Credit per remaining minute, rounded up (D02, R12). */
export function instantPrice(finishesAt: number, now: number): number {
  return Math.max(0, Math.ceil((finishesAt - now) / 60_000));
}

export function Stat({ label, value, next }: { label: string; value: string; next?: string | null }) {
  return (
    <div className="stat">
      <span className="stat-label">{label}</span>
      <span className="mono">{value}</span>
      {next ? <span className="mono stat-next"><Icon name="arrowRight" /> {next}</span> : null}
    </div>
  );
}

export function attributeLabel(key: string): string {
  const k = `attr.${key}`;
  const text = t(k);
  return text === k ? key : text;
}

export function formatAttribute(key: string, value: number): string {
  if (key.endsWith("Pct") || key.endsWith("pct")) return `${value}%`;
  if (key === "warningSec") return formatDuration(value * 1000);
  return formatCompact(value);
}
